import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthContext } from '../App';
import billingServices from '../services/billingServices';

const AccessControlContext = createContext(null);

export const useAccessControl = () => {
  const context = useContext(AccessControlContext);
  if (!context) {
    throw new Error('useAccessControl must be used within AccessControlProvider');
  }
  return context;
};

export const AccessControlProvider = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserStatus = async (skipBroadcast = false) => {
    // Don't fetch if user is not authenticated
    if (!isAuthenticated) {
      setUserStatus(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const status = await billingServices.getUserStatus();
      setUserStatus(status);
      
      // Broadcast status update to other tabs (unless this update came from another tab)
      if (!skipBroadcast && typeof BroadcastChannel !== 'undefined') {
        try {
          const channel = new BroadcastChannel('user_status_channel');
          channel.postMessage({ type: 'status_update', status });
          channel.close();
        } catch (err) {
          console.warn('BroadcastChannel not supported:', err);
        }
      }
    } catch (err) {
      // If we get a 401, the user is likely not authenticated - clear status silently
      if (err.message && (err.message.includes('401') || err.message.includes('Unauthorized'))) {
        console.log('[AccessControl] Got 401, clearing user status');
        setUserStatus(null);
        setError(null); // Don't show error for auth failures
      } else {
        console.error('Failed to fetch user status:', err);
        setError(err.message);
        // Don't block the app on error - assume full access until we know otherwise
        setUserStatus({
          category: 'active_with_credits',
          permissions: {
            can_create_project: true,
            can_upload_file: true,
            can_start_server: true,
            can_ai_query: true,
            can_add_credits: true,
            can_view_only: true
          },
          metadata: {},
          action_needed: null
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch when authenticated
    if (isAuthenticated) {
      fetchUserStatus();
      
      // Refresh status every 5 minutes
      const interval = setInterval(fetchUserStatus, 5 * 60 * 1000);
      
      // Listen for status updates from other tabs
      let channel;
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          channel = new BroadcastChannel('user_status_channel');
          channel.onmessage = (event) => {
            if (event.data.type === 'status_update') {
              console.log('[AccessControl] Received status update from another tab');
              setUserStatus(event.data.status);
            }
          };
        } catch (err) {
          console.warn('BroadcastChannel not supported:', err);
        }
      }
      
      return () => {
        clearInterval(interval);
        if (channel) {
          channel.close();
        }
      };
    } else {
      // Clear status when not authenticated
      setUserStatus(null);
      setLoading(false);
    }
  }, [isAuthenticated]);

  const can = (feature) => {
    if (!userStatus || !userStatus.permissions) return false;
    return userStatus.permissions[`can_${feature}`] === true;
  };

  const value = {
    userStatus,
    loading,
    error,
    can,
    refresh: fetchUserStatus,
    category: userStatus?.category,
    credits: userStatus?.metadata?.credits,
    usage: userStatus?.metadata?.usage_this_cycle,
    actionNeeded: userStatus?.action_needed
  };

  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
};

